import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCurrentMenuId, getRestaurantId } from 'redux/app';
import { getBrandCurrency } from 'redux/brand';
import { getProductById } from 'redux/menu';
import { getOrderCart, updateOrder } from 'redux/order';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { usePreviewOrderMutation } from 'services/order/order.endpoints';
import { ChannelId } from 'services/order/order.type';
import { ConsumptionModeType } from 'services/consumptionMode/consumptionMode.type';
import i18n from 'core/InitialiseThirdParties/i18n';
import { computePrice } from 'utils/price';
import { parseProductId } from './utils';

export const useProductVM = () => {
    const { productId } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const product = useAppSelector(getProductById(parseProductId(productId)));
    const currency = useAppSelector(getBrandCurrency);
    const restaurantId = useAppSelector(getRestaurantId);
    const currentMenuId = useAppSelector(getCurrentMenuId);
    const orderCart = useAppSelector(getOrderCart);

    const [previewOrder, { data, isLoading }] = usePreviewOrderMutation();

    const handleAddProduct = useCallback(async () => {
        if (product && restaurantId && currentMenuId) {
            const orderCartFormatted = orderCart.map((cartProduct) => ({
                productId: cartProduct.productId,
                quantity: cartProduct.quantity,
                steps: cartProduct.steps,
            }));

            await previewOrder({
                restaurantId,
                channelId: ChannelId.WEB,
                consumptionMode: ConsumptionModeType.MODE_SIT_IN,
                menuId: currentMenuId,
                cart: [
                    ...orderCartFormatted,
                    {
                        productId: product.productId,
                        quantity: 1,
                        steps: [],
                    },
                ],
            });
            navigate(-1);
        }
    }, [product, restaurantId, currentMenuId, orderCart, previewOrder, navigate]);

    useEffect(() => {
        if (data) {
            dispatch(updateOrder(data));
        }
    }, [dispatch, data]);

    const goBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const amountWithCurrency =
        currency && product?.price ? computePrice(i18n, product.price, currency) : '';

    return {
        isError: !product,
        product,
        currency,
        isLoading,
        amountWithCurrency,
        handleAddProduct,
        goBack,
    };
};
